import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Close from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { fetchViaPost } from "../helpers/fetchViaPost";
import { getFixtureScore } from "../helpers/leagueMatchdayAnalysis";
import { dateKey } from "../helpers/gamesPeriod";
import {
  getUserAccessLevel,
  FEATURES,
  hasFeatureAccess,
} from "../helpers/featureAccess";
import "./DayStatistics.css";
import { dayShowSteps, dayNarrative } from "../helpers/dayStatisticsNarrative";

export function DayStatisticsEntry({ rows, day, user }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  if (!hasFeatureAccess(FEATURES.DAY_STATISTICS, user)) return null;
  return (
    <>
      <Button
        startIcon={<InfoOutlined />}
        onClick={() => setOpen(true)}
        title={t("help.dayStatistics.title")}
      >
        {t("help.dayStatistics.action")}
      </Button>
      {open && (
        <DayStatistics
          rows={rows}
          day={day}
          user={user}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
export function DayStatistics({ rows, day, user, onClose }) {
  const { t, i18n } = useTranslation();
  const text = (k) => t(`help.dayStatistics.${k}`);
  const language = i18n.language?.startsWith("en") ? "en" : "de";
  const completed = useMemo(
    () => rows.filter((r) => getFixtureScore(r)),
    [rows]
  );
  const [ids, setIds] = useState(() => completed.map((r) => Number(r.id)));
  const [data, setData] = useState(null),
    [error, setError] = useState(false),
    [busy, setBusy] = useState(false),
    [ai, setAi] = useState(null),
    [aiError, setAiError] = useState(false),
    [aiBusy, setAiBusy] = useState(false);
  const [cohort, setCohort] = useState("all"),
    [aiSteps, setAiSteps] = useState(null);
  const [mode, setMode] = useState("count"),
    [duration, setDuration] = useState(20),
    [show, setShow] = useState(-1);
  const steps = aiSteps || dayShowSteps;
  const sectionIndex =
    show < 0
      ? -1
      : ["outcomes", "goals", "quality"].indexOf(steps[show]?.section);
  const selected = data?.cohorts?.[cohort] || (cohort === "all" ? data : null);
  const words = (de, en) => (language === "en" ? en : de);
  const cohortLabel = (k) =>
    ({
      all: words("Alle Spiele", "All games"),
      statistics: words("Mit Statistikmodell", "With statistics model"),
      should: words("Mit Should Goals", "With Should Goals"),
      common: words("Gemeinsame Vergleichsmenge", "Common comparison sample"),
    }[k]);
  const sections = useRef([]),
    request = useRef(0);
  const signature = JSON.stringify([...ids].sort((a, b) => a - b));
  const date = dateKey(day);
  useEffect(() => {
    const version = ++request.current;
    let cancelled = false;
    setData(null);
    setCohort("all");
    setAiSteps(null);
    setError(false);
    setBusy(true);
    setAi(null);
    setAiError(false);
    setShow(-1);
    const controller = new AbortController();
    fetchViaPost(
      "day-statistics",
      JSON.stringify({ date, fixture_ids: JSON.parse(signature) }),
      { signal: controller.signal }
    )
      .then(async (r) => {
        const result = await r.json();
        if (!r.ok || result.success !== true) throw Error("load");
        return result;
      })
      .then((result) => {
        if (!cancelled && version === request.current) setData(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
      request.current++;
      controller.abort();
    };
  }, [date, signature, language]);
  useEffect(() => {
    if (show < 0) return undefined;
    setCohort(steps[show].cohort);
    sections.current[sectionIndex]?.scrollIntoView?.({
      behavior: "smooth",
      block: "center",
    });
    const timer = setTimeout(
      () => setShow((s) => (s >= steps.length - 1 ? -1 : s + 1)),
      duration * 1000
    );
    return () => clearTimeout(timer);
  }, [show, duration, steps, sectionIndex]);
  const explain = async () => {
    const version = request.current;
    setAiBusy(true);
    setAiError(false);
    try {
      const r = await fetchViaPost(
        "day-statistics",
        JSON.stringify({
          date,
          fixture_ids: ids,
          explain: true,
          language,
          cohort,
        })
      );
      const d = await r.json();
      if (!r.ok || !d.success || !d.ai?.explanation)
        throw Error(d.ai_error || d.code || "ai");
      if (version === request.current) {
        setAi(d.ai.explanation);
        if (d.ai.steps?.length) {
          setAiSteps(d.ai.steps);
          setShow(0);
        }
      }
    } catch (e) {
      if (version === request.current) setAiError(e.message);
    } finally {
      setAiBusy(false);
    }
  };
  const fmt = (v, d = 2) =>
    v == null || !Number.isFinite(v)
      ? "—"
      : v.toLocaleString(language === "de" ? "de-DE" : "en-GB", {
          maximumFractionDigits: d,
          minimumFractionDigits: d,
        });
  const signed = (v, d = 2) =>
    v == null || !Number.isFinite(v) ? "—" : `${v > 0 ? "+" : ""}${fmt(v, d)}`;
  const differenceClass = (v) =>
    show >= 0 && Number.isFinite(v)
      ? v > 0
        ? "ds-positive"
        : v < 0
        ? "ds-negative"
        : "ds-neutral"
      : "";
  const percent = (v) => (v == null ? "—" : `${fmt(v * 100, 1)} %`);
  const section = (k) => selected?.sections?.[k]?.available;
  const actual = section("goals")?.actual;
  const sourceLabel = (k) => (k === "should" ? "Should Goals" : text(k));
  const coverage = (s, k) =>
    `${s?.models?.[k]?.count ?? 0}/${selected?.finished_fixtures ?? 0}`;
  const outcome = (v, count, actualValue = false) =>
    v == null || !count
      ? "—"
      : ["count", "difference"].includes(mode)
      ? fmt(v, actualValue ? 0 : 1)
      : mode === "probability"
      ? percent(v / count)
      : v > 0
      ? fmt(count / v)
      : "—";
  const models = ["result", "statistics", "market"];
  const missing = Object.values(data?.timing || {}).reduce(
    (sum, r) => sum + r.retrospective + r.unverified,
    0
  );
  const panel = (index, key, children) => (
    <section
      ref={(el) => (sections.current[index] = el)}
      className={`ds-panel ${sectionIndex === index ? "ds-focus" : ""}`}
    >
      <h2>{text(key)}</h2>
      {children}
    </section>
  );
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ className: "day-statistics", sx: { maxWidth: 1440, m: 2 } }}
    >
      <header className="ds-header">
        <div>
          <h1>{text("title")}</h1>
          <span>
            {date.split("-").reverse().join(".")} ·{" "}
            {selected?.finished_fixtures ?? completed.length}{" "}
            {text("completed")}
          </span>
        </div>
        <IconButton onClick={onClose} aria-label={text("close")}>
          <Close />
        </IconButton>
      </header>
      <DialogContent>
        <details className="ds-selection">
          <summary>
            {text("selection")} ({ids.length}/{completed.length})
          </summary>
          <div>
            {completed.map((r) => (
              <FormControlLabel
                key={r.id}
                control={
                  <Checkbox
                    checked={ids.includes(Number(r.id))}
                    onChange={(_, checked) =>
                      setIds((prev) =>
                        checked
                          ? [...prev, Number(r.id)]
                          : prev.filter((id) => id !== Number(r.id))
                      )
                    }
                  />
                }
                label={
                  r.name ||
                  `${r.localTeam?.name || ""} – ${r.visitorTeam?.name || ""}`
                }
              />
            ))}
          </div>
        </details>
        {busy && (
          <div role="status">
            <CircularProgress size={22} /> {text("loading")}
          </div>
        )}
        {error && <Alert severity="error">{text("error")}</Alert>}
        {data && (
          <>
            <div
              className="ds-cohorts"
              aria-label={words("Vergleichsauswahl", "Comparison sample")}
            >
              <ToggleButtonGroup
                exclusive
                value={cohort}
                size="small"
                onChange={(_, v) => {
                  if (v) {
                    setCohort(v);
                    setShow(-1);
                    setAi(null);
                  }
                }}
              >
                {["all", "statistics", "should", "common"].map((k) => (
                  <ToggleButton
                    key={k}
                    value={k}
                    disabled={k !== "all" && !data.cohorts?.[k]}
                  >
                    {cohortLabel(k)} (
                    {data.cohorts?.[k]?.finished_fixtures ??
                      (k === "all" ? data.finished_fixtures : 0)}
                    )
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <p>
                {cohortLabel(cohort)}: {selected?.finished_fixtures ?? 0}/
                {data.finished_fixtures}.{" "}
                {words(
                  "Tagesbilanz und alle Tabellen werden gemeinsam umgeschaltet.",
                  "Day totals and all tables switch together."
                )}
              </p>
            </div>
            <div className="ds-kpis">
              {[
                ["games", actual?.fixtures_played],
                ["homeWins", actual?.home_wins],
                ["draws", actual?.draws],
                ["awayWins", actual?.away_wins],
                ["homeGoals", actual?.home_goals],
                ["awayGoals", actual?.away_goals],
                ["average", actual?.goal_average],
                ["homeFactor", actual?.home_advantage],
              ].map(([k, v]) => (
                <div key={k}>
                  <span>{text(k)}</span>
                  <strong>
                    {fmt(v, ["average", "homeFactor"].includes(k) ? 2 : 0)}
                  </strong>
                </div>
              ))}
            </div>
            <p className="ds-note">
              {text("coverage")} {text("prematch")}{" "}
              {missing > 0 && text("excludedTiming")}{" "}
              {data.issues?.length > 0 && text("partialError")}
            </p>
            {data.cohorts && (
              <details className="ds-gaps">
                <summary>
                  {words(
                    "Fehlende Vergleichsgrundlagen",
                    "Missing comparison data"
                  )}
                </summary>
                {["statistics", "should"].map((k) => {
                  const included = new Set(data.cohorts[k]?.fixture_ids || []);
                  const excluded = completed.filter(
                    (r) =>
                      ids.includes(Number(r.id)) && !included.has(Number(r.id))
                  );
                  return (
                    <p key={k}>
                      <strong>
                        {cohortLabel(k)}: {excluded.length}{" "}
                        {words("nicht enthalten", "excluded")}
                      </strong>{" "}
                      ·{" "}
                      {excluded
                        .map(
                          (r) =>
                            r.name ||
                            `${r.localTeam?.name || ""} – ${
                              r.visitorTeam?.name || ""
                            }`
                        )
                        .join(", ")}
                      .{" "}
                      {k === "should"
                        ? words(
                            "Keine verwendbaren gespeicherten Should Goals. Ob Anbieterstatistiken fehlen oder die Verarbeitung offen ist, ist damit noch nicht bewiesen.",
                            "No usable stored Should Goals. This alone does not establish whether provider statistics are missing or processing is pending."
                          )
                        : words(
                            "Keine vollständige, zeitlich belegte Pre-Match-Prognose mit Torerwartungen und 1/X/2-Werten.",
                            "No complete, timestamp-verified pre-match forecast with expected goals and 1/X/2 values."
                          )}
                    </p>
                  );
                })}
              </details>
            )}
            {show >= 0 && (
              <Alert
                severity="info"
                className="ds-narration"
                aria-live="polite"
              >
                <strong>
                  {aiSteps
                    ? words("KI-Analyse", "AI analysis")
                    : words(
                        "Datenbasierte Erläuterung",
                        "Data-based explanation"
                      )}{" "}
                  · {show + 1}/{steps.length}
                </strong>
                <p>
                  {steps[show]?.explanation ||
                    dayNarrative(
                      data,
                      steps[show]?.cohort,
                      steps[show]?.section,
                      language
                    )}
                </p>
                <Button
                  onClick={() =>
                    setShow((s) => (s >= steps.length - 1 ? -1 : s + 1))
                  }
                >
                  {words("Weiter", "Next")}
                </Button>
              </Alert>
            )}
            <div className="ds-grid">
              {panel(
                0,
                "outcomes",
                <>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={mode}
                    onChange={(_, v) => v && setMode(v)}
                  >
                    {["count", "probability", "fairOdds", "difference"].map(
                      (k) => (
                        <ToggleButton key={k} value={k}>
                          {text(k)}
                        </ToggleButton>
                      )
                    )}
                  </ToggleButtonGroup>
                  <table>
                    <thead>
                      <tr>
                        <th>{text("model")}</th>
                        <th>{text("homeWins")}</th>
                        <th>{text("draws")}</th>
                        <th>{text("awayWins")}</th>
                        <th>n</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="ds-actual">
                        <th>{text("actual")}</th>
                        {[
                          actual?.home_wins,
                          actual?.draws,
                          actual?.away_wins,
                        ].map((v, i) => (
                          <td key={i}>
                            {outcome(v, actual?.fixtures_played, true)}
                          </td>
                        ))}
                        <td>{actual?.fixtures_played ?? 0}</td>
                      </tr>
                      {models.map((k) => {
                        const m = section("outcomes")?.models[k];
                        return (
                          <tr key={k}>
                            <th>{sourceLabel(k)}</th>
                            {["home", "draw", "away"].map((s) => (
                              <td
                                key={s}
                                className={
                                  mode === "difference"
                                    ? differenceClass(
                                        actual?.[
                                          {
                                            home: "home_wins",
                                            draw: "draws",
                                            away: "away_wins",
                                          }[s]
                                        ] - m?.values?.[s]
                                      )
                                    : ""
                                }
                              >
                                {mode === "difference"
                                  ? m?.count === selected?.finished_fixtures
                                    ? signed(
                                        actual?.[
                                          {
                                            home: "home_wins",
                                            draw: "draws",
                                            away: "away_wins",
                                          }[s]
                                        ] - m?.values?.[s],
                                        1
                                      )
                                    : "—"
                                  : outcome(m?.values?.[s], m?.count)}
                              </td>
                            ))}
                            <td>{coverage(section("outcomes"), k)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {mode === "fairOdds" && (
                    <p className="ds-note">{text("empiricalOdds")}</p>
                  )}
                </>
              )}
              {panel(
                1,
                "goals",
                <table>
                  <thead>
                    <tr>
                      <th>{text("model")}</th>
                      {[
                        "homeGoals",
                        "awayGoals",
                        "totalGoals",
                        "average",
                        "homeFactor",
                      ].map((k) => (
                        <th key={k}>{text(k)}</th>
                      ))}
                      <th>n</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="ds-actual">
                      <th>{text("actual")}</th>
                      {[
                        actual?.home_goals,
                        actual?.away_goals,
                        actual?.goals,
                        actual?.goal_average,
                        actual?.home_advantage,
                      ].map((v, i) => (
                        <td key={i}>{fmt(v, i < 3 ? 0 : 2)}</td>
                      ))}
                      <td>{actual?.fixtures_played ?? 0}</td>
                    </tr>
                    {["result", "statistics", "should", "market"].map((k) => {
                      const s = section(k === "should" ? "should" : "goals"),
                        key = k === "should" ? "adaptive_should" : k,
                        m = s?.models?.[key];
                      return (
                        <tr key={k}>
                          <th>{sourceLabel(k)}</th>
                          {[
                            "home",
                            "away",
                            "total",
                            "average",
                            "home_factor",
                          ].map((f) => (
                            <td key={f}>{fmt(m?.values?.[f])}</td>
                          ))}
                          <td>{coverage(s, key)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {panel(
              2,
              "quality",
              <>
                <table>
                  <thead>
                    <tr>
                      <th>{text("model")}</th>
                      <th>Average Expected Probability</th>
                      <th>Average Occurred Probability</th>
                      <th>{text("difference")}</th>
                      <th>{text("expectedDeviation")}</th>
                      <th>{text("occurredDeviation")}</th>
                      <th>Δ {words("Torabweichung", "goal deviation")}</th>
                      <th>{text("coverageShort")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {models.map((k) => {
                      const p = section("probabilities")?.models[k],
                        d = section("deviation")?.models[k],
                        delta = p?.values
                          ? p.values.occurred - p.values.expected
                          : null;
                      return (
                        <tr key={k}>
                          <th>{sourceLabel(k)}</th>
                          <td>{percent(p?.values?.expected)}</td>
                          <td>{percent(p?.values?.occurred)}</td>
                          <td
                            className={differenceClass(delta)}
                            title={words(
                              "Eingetreten minus erwartet, auf der Prozentskala",
                              "Observed minus expected, on the percentage scale"
                            )}
                          >
                            {signed(delta == null ? null : delta * 100, 1)}
                          </td>
                          <td>{fmt(d?.values?.expected)}</td>
                          <td>{fmt(d?.values?.occurred)}</td>
                          <td
                            className={differenceClass(
                              d?.values?.expected != null &&
                                d?.count === d?.values?.expected_count
                                ? d.values.occurred - d.values.expected
                                : NaN
                            )}
                          >
                            {d?.values?.expected != null &&
                            d?.count === d?.values?.expected_count
                              ? signed(d.values.occurred - d.values.expected, 2)
                              : "—"}
                          </td>
                          <td>{`${p?.count ?? 0} / ${
                            d?.values?.expected_count ?? 0
                          } / ${d?.count ?? 0}`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="ds-note">
                  {text("qualityNote")}{" "}
                  {words(
                    "Δ = eingetreten minus erwartet. Während der Show: Grün = positiv, Rot = negativ; das Vorzeichen allein bewertet keine Modellgüte.",
                    "Δ = observed minus expected. During the show: green = positive, red = negative; the sign alone does not rate model quality."
                  )}
                </p>
              </>
            )}
            <div className="ds-show">
              <div>
                <strong>{text("show")}</strong>
                <p>
                  {text("showNote")}{" "}
                  {words(
                    "Zeit je Schritt; mit Weiter jederzeit fortfahren.",
                    "Time per step; use Next to advance at any time."
                  )}
                </p>
              </div>
              <ToggleButtonGroup
                exclusive
                value={duration}
                size="small"
                onChange={(_, v) => v && setDuration(v)}
              >
                <ToggleButton value={20}>20 {text("seconds")}</ToggleButton>
                <ToggleButton value={60}>1 Min</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                disabled={!data.finished_fixtures}
                onClick={() => {
                  setAiSteps(null);
                  setShow(show < 0 ? 0 : -1);
                }}
              >
                {text(show < 0 ? "startShow" : "stopShow")}
              </Button>
              {getUserAccessLevel(user) === "admin" && (
                <Button
                  disabled={aiBusy || !data.finished_fixtures}
                  onClick={explain}
                >
                  {aiBusy ? text("loading") : text("ai")}
                </Button>
              )}
            </div>
            {aiError && (
              <Alert severity="warning">
                {text("aiError")}{" "}
                {words(
                  "Die zahlenbasierte Show bleibt verfügbar.",
                  "The data-based show remains available."
                )}{" "}
                {aiError === "AI_NOT_CONFIGURED"
                  ? words(
                      "KI-Zugang oder Modell ist nicht konfiguriert.",
                      "AI access or model is not configured."
                    )
                  : aiError === "AI_RATE_LIMIT"
                  ? words(
                      "KI-Anbieterlimit erreicht.",
                      "AI provider limit reached."
                    )
                  : ""}
              </Alert>
            )}

            {ai && <Alert severity="info">{ai}</Alert>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
